import { Textarea, TextareaProps } from '@cloudscape-design/components';
import { useRef, useState, useEffect, useCallback } from 'react';

/**
 * Wraps Cloudscape Textarea to handle IME composition events correctly.
 * Fixes Samsung keyboard (One UI) reverse-typing bug where characters appear
 * reversed because React re-renders set textarea.value during active
 * composition, disrupting the IME buffer.
 *
 * Buffers value locally during composition; propagates onChange only after
 * compositionEnd.
 */
const CompositionSafeTextarea = (props: TextareaProps) => {
  const { value, onChange, ...rest } = props;
  const isComposing = useRef(false);
  const localValueRef = useRef(value);
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    if (!isComposing.current) {
      localValueRef.current = value;
      setLocalValue(value);
    }
  }, [value]);

  const handleChange: NonNullable<TextareaProps['onChange']> = useCallback(
    (event) => {
      localValueRef.current = event.detail.value;
      setLocalValue(event.detail.value);
      if (!isComposing.current) {
        onChange?.(event);
      }
    },
    [onChange],
  );

  // Flush any buffered value on unmount (e.g., question navigation mid-composition)
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    return () => {
      if (isComposing.current) {
        onChangeRef.current?.({
          detail: { value: localValueRef.current },
        } as CustomEvent<TextareaProps.ChangeDetail>);
      }
    };
  }, []);

  return (
    <div
      onCompositionStart={() => {
        isComposing.current = true;
      }}
      onCompositionEnd={() => {
        isComposing.current = false;
        // Use ref to avoid stale closure — localValue state may not have
        // flushed yet if React batched the preceding handleChange update.
        onChange?.({
          detail: { value: localValueRef.current },
        } as CustomEvent<TextareaProps.ChangeDetail>);
      }}
    >
      <Textarea {...rest} value={localValue} onChange={handleChange} />
    </div>
  );
};

export default CompositionSafeTextarea;
